// htmx, for the Sprig components (the Work, Results and blog listings, the
// services search, the directory's search). The site bundles it as a chunk
// that loads only when a page carries a component, in place of the copy Sprig
// used to register on those pages (turned off in modules/sitemodule, see
// docs/prd/2026-10-31-router-page-changes.md, R7).
//
// Two things the page lifecycle has to do that a script tag did for free:
//
// - process the markup. htmx processes the whole document once, at
//   DOMContentLoaded; a container swapped in later, or this chunk arriving
//   after that moment, needs `htmx.process(root)`.
// - restore its history entries. A filter or a search pushes its own URL
//   (htmx's `{ htmx: true }` state, or the listing's own `{ listing: true }`
//   from features/listing.js); with no history cache those entries are
//   restored by fetching the URL again: through the router, with the fade,
//   when it is running, by a full load otherwise. The router's own popstate
//   handling skips entries that are not its own, so this module takes them.
import { mountWhenPresent } from "../site/mount.js";

// Anything Sprig renders carries an hx- attribute; `sprig` is the marker on a
// component's root.
export const SPRIG_MARKUP = "[sprig], [hx-get], [hx-post], [hx-trigger], [data-hx-get], [data-hx-post]";

let loading;

// Resolves with htmx, importing it once; a page without Sprig never pays for it.
export function ensureHtmx() {
  if (window.htmx) return Promise.resolve(window.htmx);
  loading ??= import("htmx.org").then((module) => {
    const htmx = module.default;
    htmx.config.historyCacheSize = 0;
    htmx.config.refreshOnHistoryMiss = true;
    window.htmx = htmx;
    return htmx;
  });
  return loading;
}

// A shared page feature: load htmx where a page has a component and process the container.
export function mountSprig({ root }) {
  return mountWhenPresent(root, SPRIG_MARKUP, ensureHtmx, (htmx) => {
    htmx.process(root);
  });
}

// Back or forward onto an entry a Sprig component or a listing pushed: the
// page is fetched again at that URL (the search or filter is in the query
// string, which the listing renders). htmx's own handler only runs when htmx
// was present at DOMContentLoaded, which a chunk never is.
export function restoreSprigHistory(event) {
  if (!event.state || !(event.state.htmx || event.state.listing)) return;
  const swup = window.growthcurve?.swup;
  if (swup) swup.navigate(location.pathname + location.search, { history: "replace" });
  else location.reload();
}
